/**
 * TeamCalendarView
 *
 * Overlapping team member calendar showing tasks from multiple assignees.
 * Left panel: member selection with checkboxes and colored dots.
 * Right panel: week calendar with tasks colored by member.
 */

import { useMemo, useState, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  CheckSquare,
  Square,
} from 'lucide-react';
import clsx from 'clsx';
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  eachDayOfInterval,
  isSameDay,
  isToday,
  getDay,
} from 'date-fns';
import { it } from 'date-fns/locale';

import type { Task } from '@/types';
import { useViewData } from '@/hooks/useViewData';
import { useAppStore } from '@/store/appStore';
import { getInitials, getAvatarColor } from '@/utils/formatters';
import { ViewToolbar } from '@/components/shared/view-toolbar';
import { isCompletedStatus } from '@/constants/taskStatuses';

// Member color palette
const memberColors = [
  { bg: 'bg-accent-violet', bgLight: 'bg-accent-violet/15', border: 'border-accent-violet/40', text: 'text-accent-violet', dot: 'bg-accent-violet' },
  { bg: 'bg-accent-blue', bgLight: 'bg-accent-blue/15', border: 'border-accent-blue/40', text: 'text-accent-blue', dot: 'bg-accent-blue' },
  { bg: 'bg-accent-emerald', bgLight: 'bg-accent-emerald/15', border: 'border-accent-emerald/40', text: 'text-accent-emerald', dot: 'bg-accent-emerald' },
  { bg: 'bg-accent-rose', bgLight: 'bg-accent-rose/15', border: 'border-accent-rose/40', text: 'text-accent-rose', dot: 'bg-accent-rose' },
  { bg: 'bg-accent-amber', bgLight: 'bg-accent-amber/15', border: 'border-accent-amber/40', text: 'text-accent-amber', dot: 'bg-accent-amber' },
  { bg: 'bg-cyan-500', bgLight: 'bg-cyan-500/15', border: 'border-cyan-500/40', text: 'text-cyan-500', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-500', bgLight: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-500', dot: 'bg-orange-500' },
];

interface MemberInfo {
  name: string;
  tasks: Task[];
  colorIndex: number;
}

export function TeamCalendarView() {
  const { tasks, onTaskClick } = useViewData();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Group tasks by assignee
  const memberMap = useMemo(() => {
    const map = new Map<string, Task[]>();
    tasks.forEach((task) => {
      (task.assignees || []).forEach((assignee) => {
        if (!map.has(assignee)) {
          map.set(assignee, []);
        }
        map.get(assignee)!.push(task);
      });
    });
    return map;
  }, [tasks]);

  // Build member list with color assignments
  const members: MemberInfo[] = useMemo(() => {
    const result: MemberInfo[] = [];
    let colorIdx = 0;
    const sorted = Array.from(memberMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    for (const [name, memberTasks] of sorted) {
      result.push({
        name,
        tasks: memberTasks,
        colorIndex: colorIdx % memberColors.length,
      });
      colorIdx++;
    }
    return result;
  }, [memberMap]);

  // Auto-select all members on first load
  if (!initialized && members.length > 0) {
    setSelectedMembers(new Set(members.map((m) => m.name)));
    setInitialized(true);
  }

  // Week days
  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );
  const weekEnd = useMemo(
    () => endOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );
  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd]
  );

  const weekLabel = useMemo(() => {
    return `${format(weekStart, 'd MMM', { locale: it })} - ${format(weekEnd, 'd MMM yyyy', { locale: it })}`;
  }, [weekStart, weekEnd]);

  // Navigation
  const goToNext = useCallback(() => setCurrentDate((d) => addWeeks(d, 1)), []);
  const goToPrev = useCallback(() => setCurrentDate((d) => subWeeks(d, 1)), []);
  const goToToday = useCallback(() => setCurrentDate(new Date()), []);

  // Member selection
  const toggleMember = useCallback((name: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedMembers(new Set(members.map((m) => m.name)));
  }, [members]);

  const deselectAll = useCallback(() => {
    setSelectedMembers(new Set());
  }, []);

  // Get tasks for a specific day, filtered by selected members
  const getTasksForDay = useCallback(
    (date: Date) => {
      const result: { task: Task; memberName: string; colorIndex: number }[] = [];
      const seen = new Set<string>(); // Avoid showing same task multiple times

      members.forEach((member) => {
        if (!selectedMembers.has(member.name)) return;

        member.tasks.forEach((task) => {
          if (seen.has(task.id)) return;
          if (!task.dueDate) return;

          if (isSameDay(new Date(task.dueDate), date)) {
            result.push({
              task,
              memberName: member.name,
              colorIndex: member.colorIndex,
            });
            seen.add(task.id);
          }
        });
      });

      return result;
    },
    [members, selectedMembers]
  );

  // Count total visible tasks
  const totalVisibleTasks = useMemo(() => {
    return weekDays.reduce((sum, day) => sum + getTasksForDay(day).length, 0);
  }, [weekDays, getTasksForDay]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      {/* Toolbar */}
      <ViewToolbar
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        icon={<CalendarIcon className="w-4 h-4 text-accent-violet" />}
        title="Calendario Team"
        subtitle={`${totalVisibleTasks} task questa settimana`}
        rightContent={
          <div className="flex items-center flex-wrap justify-end gap-2">
            {/* Today button */}
            <button
              onClick={goToToday}
              className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-accent-violet bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
            >
              Oggi
            </button>

            {/* Navigation */}
            <div className="flex items-center gap-1 bg-surface-secondary rounded-lg border border-border-primary">
              <button
                onClick={goToPrev}
                className="p-1.5 hover:bg-surface-hover rounded-l-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-text-tertiary" />
              </button>
              <span className="px-3 py-1 text-sm font-medium text-text-primary min-w-[180px] text-center">
                {weekLabel}
              </span>
              <button
                onClick={goToNext}
                className="p-1.5 hover:bg-surface-hover rounded-r-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>
        }
      />

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Member Selection */}
        <div className="w-64 shrink-0 border-r border-border-primary bg-surface-secondary/30 flex flex-col overflow-hidden">
          {/* Panel Header */}
          <div className="p-3 border-b border-border-primary">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-text-tertiary" />
              <span className="text-[12px] font-semibold text-text-primary">Membri</span>
              <span className="text-[10px] text-text-quaternary tabular-nums">{members.length}</span>
            </div>

            {/* Select/Deselect buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={selectAll}
                className="flex-1 px-2 py-1 rounded text-[10px] font-medium text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors border border-border-primary"
              >
                Seleziona tutti
              </button>
              <button
                onClick={deselectAll}
                className="flex-1 px-2 py-1 rounded text-[10px] font-medium text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors border border-border-primary"
              >
                Deseleziona tutti
              </button>
            </div>
          </div>

          {/* Member List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {members.map((member) => {
              const isSelected = selectedMembers.has(member.name);
              const color = memberColors[member.colorIndex];
              return (
                <button
                  key={member.name}
                  onClick={() => toggleMember(member.name)}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors',
                    isSelected
                      ? 'bg-surface-primary hover:bg-surface-hover'
                      : 'hover:bg-surface-tertiary/30 opacity-50'
                  )}
                >
                  {/* Checkbox */}
                  {isSelected ? (
                    <CheckSquare className={clsx('w-4 h-4 shrink-0', color.text)} />
                  ) : (
                    <Square className="w-4 h-4 shrink-0 text-text-quaternary" />
                  )}

                  {/* Color dot */}
                  <div className={clsx('w-2.5 h-2.5 rounded-full shrink-0', color.dot)} />

                  {/* Avatar */}
                  <div
                    className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0',
                      getAvatarColor(member.name)
                    )}
                  >
                    {getInitials(member.name)}
                  </div>

                  {/* Name & task count */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-text-primary truncate">
                      {member.name}
                    </p>
                    <p className="text-[10px] text-text-quaternary">
                      {member.tasks.length} task
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel - Week Calendar */}
        <div className="flex-1 overflow-auto p-4">
          <div className="h-full flex gap-2">
            {weekDays.map((day) => {
              const dayTasks = getTasksForDay(day);
              const todayMatch = isToday(day);
              const isWeekend = getDay(day) === 0 || getDay(day) === 6;

              return (
                <div
                  key={day.toISOString()}
                  className="flex-1 flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden"
                >
                  {/* Day Header */}
                  <div
                    className={clsx(
                      'px-3 py-2 border-b border-border-primary text-center',
                      todayMatch && 'bg-accent-violet/10'
                    )}
                  >
                    <div className="text-[10px] text-text-tertiary uppercase">
                      {format(day, 'EEE', { locale: it })}
                    </div>
                    <div
                      className={clsx(
                        'text-lg font-semibold',
                        todayMatch
                          ? 'text-accent-violet'
                          : isWeekend
                            ? 'text-text-quaternary'
                            : 'text-text-primary'
                      )}
                    >
                      {format(day, 'd')}
                    </div>
                    <div className="text-[10px] text-text-quaternary">
                      {dayTasks.length > 0 ? `${dayTasks.length} task` : ''}
                    </div>
                  </div>

                  {/* Day Tasks */}
                  <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
                    {dayTasks.length === 0 && (
                      <div className="text-center py-4 text-text-quaternary text-[10px]">
                        Nessun task
                      </div>
                    )}
                    {dayTasks.map(({ task, memberName, colorIndex }) => {
                      const color = memberColors[colorIndex];
                      const completed = isCompletedStatus(task.status);
                      return (
                        <div
                          key={task.id}
                          onClick={() => onTaskClick?.(task)}
                          className={clsx(
                            'px-2 py-1.5 rounded-md border-l-2 cursor-pointer transition-all',
                            color.bgLight,
                            color.border,
                            completed && 'opacity-50 line-through',
                            'hover:shadow-sm'
                          )}
                        >
                          <p className="text-[10px] font-medium text-text-primary truncate">
                            {task.name}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <div
                              className={clsx(
                                'w-2 h-2 rounded-full shrink-0',
                                color.dot
                              )}
                            />
                            <span className={clsx('text-[9px] truncate', color.text)}>
                              {memberName}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
